/**
 * QueryState — гейт isError для секции, питаемой одним query.
 *
 * Без этого `isError` неотличим от пустых данных (см. экраны people/org/...):
 * упавший запрос рендерит тот же "пусто", что и honestly-empty список. 403
 * (нет прав у роли) отдельно от прочих сбоев — повтор его не починит, поэтому
 * кнопки «Повторить» для него нет. Оборачивать нужно СЕКЦИЮ, которую питает
 * запрос, а не всю страницу — см. использование по экранам.
 */
import type { ReactNode } from 'react'
import type { UseQueryResult } from '@tanstack/react-query'
import { Text } from 'tamagui'

import { ApiError } from '../api/client'
import { useApp } from '../i18n'
import { Button, Card } from './primitives'
import { Icon } from './Icon'

export function QueryState({
  q,
  children,
}: {
  q: Pick<UseQueryResult, 'isError' | 'error' | 'refetch'>
  children: ReactNode
}) {
  const { t } = useApp()
  if (!q.isError) return <>{children}</>

  const forbidden = q.error instanceof ApiError && q.error.status === 403

  return (
    <Card padding={20} gap={10} alignItems="center" minHeight={180} justifyContent="center">
      <Icon name="triangle-alert" size={26} color="$primary" />
      <Text fontSize={13} color="$muted" textAlign="center" maxWidth={420}>
        {forbidden
          ? t({ ru: 'Недостаточно прав для просмотра раздела', en: 'No access' })
          : t({ ru: 'Не удалось загрузить данные', en: 'Failed to load' })}
      </Text>
      {/* 403 не ретраится: повтор с теми же правами даст тот же 403 */}
      {!forbidden && (
        <Button onPress={() => q.refetch()}>{t({ ru: 'Повторить', en: 'Retry' })}</Button>
      )}
    </Card>
  )
}
